import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Grid,
  Autocomplete,
  TextField,
} from "@mui/material";

import PageContainer from "src/components/container/PageContainer";
import DashboardCard from "src/components/shared/DashboardCard";
import { useSnackBar } from "src/context/SnackBarContext";
import DataTable from "src/components/DataTable";
import Loader from "src/components/Loader";
import { postData } from "../utilities/api";
import useFetchData from "../utilities/useFetchData";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

import { IconRefresh } from "@tabler/icons";

const paymentModes = ["Cash", "UPI", "Bank", "Card"];

function SalesReport() {
  const { showSnackbar } = useSnackBar();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [selectedMode, setSelectedMode] = useState("");
  const [selectedType, setSelectedType] = useState("Quotation");

  // 0 = Both (Party / Phone), 1 = Party Only, 2 = Phone Only
  const [partyDisplayMode, setPartyDisplayMode] = useState(0);

  const togglePartyDisplayMode = () => {
    setPartyDisplayMode((prevMode) => (prevMode + 1) % 3);
  };

  const columns = [
    { field: "BillNo", headerName: "Bill No", width: 100 },
    {
      field: "Party",
      headerName:
        partyDisplayMode === 0
          ? "Party / Phone"
          : partyDisplayMode === 1
          ? "Party"
          : "Phone",
      flex: 1,
      renderHeader: () => (
        <Box
          onClick={togglePartyDisplayMode}
          sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 0.5, userSelect: "none" }}
          title="Click to toggle: Both -> Party Only -> Phone Only"
        >
          <span>
            {partyDisplayMode === 0 && "Party / Phone"}
            {partyDisplayMode === 1 && "Party"}
            {partyDisplayMode === 2 && "Phone"}
          </span>
          <IconRefresh size={14} />
        </Box>
      ),
      renderCell: (row) => {
        const name = row.Party || row.party || "Cash Customer";
        const phone = row.phone ? ` (${row.phone})` : "";
        if (partyDisplayMode === 0) {
          return `${name}${phone}`;
        } else if (partyDisplayMode === 1) {
          return name;
        } else {
          return row.phone || "-";
        }
      },
    },
    { field: "Total", headerName: "Total", flex: 1 },
    { field: "Received", headerName: "Received", flex: 1 },
    { field: "PaymentMode", headerName: "Payment Mode", flex: 1 },
  ];


  const { data, isLoading, error } = useFetchData(
    ["masterData"],
    ["/api/getMasterDatas"]
  );
  const partyData = data?.masterData?.data || [];
  const navigate = useNavigate();
  // console.log(partyData, isLoading, error);
  
  const navigateToPrint = (type) => {
    navigate("/sales_report_print", {
      state: {
        header: {
          fromDate: dayjs(fromDate).format("DD/MM/YYYY"),
          toDate: dayjs(toDate).format("DD/MM/YYYY"),
          party: selectedParty,
          branch: selectedBranch,

        },
        table: reportData,
        type: type,
      },
    });
  };
  const handleDateChange = (from, to) => {
    setFromDate(from);
    setToDate(to);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const payload = {
      branch: selectedBranch,
      party: selectedParty,
      paymentMode: selectedMode,
      fromDate: fromDate || dayjs(),
      toDate: toDate || dayjs(),
      type:selectedType
    };

    const result = await postData("/api/getSalesReport", payload);

    if (!result.success) {
      showSnackbar("error", result.message);
      setReportData([]);
    } else if (result.data.length === 0) {
      showSnackbar("warning", "No data found");
      setReportData([]);
    }else {
    
      const dataWithSi = result.data.map((row, index) => {
        const totalPaid = Number(row.Received || 0) + Number(row.BankRecieved || 0);
        let updatedReceiptType = row.PaymentMode;

        // If total paid is 0 or payment mode is missing, set to "Pending"
        if (totalPaid === 0 || !updatedReceiptType) {
          updatedReceiptType = "Pending";
        }

        return {
          si: index + 1,
          ...row,
          Received: totalPaid,
          PaymentMode: updatedReceiptType,
        };
      });

      setReportData(dataWithSi);
    }

    setLoading(false);
  };

  return (
    <>
      <Loader start={loading || isLoading} />
      <PageContainer>
        <DashboardCard>
          <Typography variant="h4" mb={2}>
            Bill Report
          </Typography>

          <Box sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="From Date"
                    format="DD/MM/YYYY"
                    value={fromDate}
                    onChange={(date) => handleDateChange(date, toDate)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="To Date"
                    format="DD/MM/YYYY"
                    minDate={fromDate}
                    value={toDate}
                    onChange={(date) => handleDateChange(fromDate, date)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={["ALL", ...(partyData?.branch_list || [])]}
                    value={selectedBranch || "ALL"}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return `${option.label || ""}`.trim();
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (
                        typeof option === "string" ||
                        typeof value === "string"
                      ) {
                        return option === value;
                      }
                      return (
                        option.label === value.label &&
                        option.name === value.name
                      );
                    }}
                    onChange={(e, val) =>
                      setSelectedBranch(val === "ALL" ? null : val)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Branch" />
                    )}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>

            {/* Remaining Grid for Party, Payment Mode, Submit Button */}
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={["ALL", ...(partyData?.customer_list || [])]}
                  value={selectedParty || "ALL"}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return `${option.label}-${option.name || ""}`.trim();
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (
                      typeof option === "string" ||
                      typeof value === "string"
                    ) {
                      return option === value;
                    }
                    return (
                      option.label === value.label && option.name === value.name
                    );
                  }}
                  onChange={(e, val) =>
                    setSelectedParty(val === "ALL" ? null : val)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Party" />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={["ALL", ...paymentModes]}
                  getOptionLabel={(option) => option}
                  value={selectedMode || "ALL"}
                  onChange={(e, val) =>
                    setSelectedMode(val === "ALL" ? "" : val)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payment Mode"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Grid>
               <Grid item xs={12} md={4}>
                <Autocomplete
                  options={["Quotation", "Delivered"]}
                  getOptionLabel={(option) => option}
                  value={selectedType || "Quotation"}
                  onChange={(e, val) =>
                    setSelectedType(val || "Quotation")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Type"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{ mt: { xs: 1.5, md: 0 } }}
                  fullWidth
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </Box>

          <DataTable columns={columns} rows={reportData} />
          {reportData.length > 0 && (
            <Box mt={3} p={2} border="1px solid #ccc" borderRadius={2}>
              <Grid container spacing={2} justifyContent="flex-end">
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Total Sales: ₹
                    {reportData
                      .reduce((sum, row) => sum + Number(row.Total || 0), 0)
                      .toFixed(2)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Total Credit: ₹
                    {reportData
                      .reduce((sum, row) => {
                        const total = Number(row.Total || 0);
                        const received = Number(row.Received || 0);
                        const credit = Math.max(0, total - received);
                        return sum + credit;
                      }, 0)
                      .toFixed(2)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Total Bank: ₹
                    {reportData
                 
                      .reduce((sum, row) => sum + Number(row.BankRecieved || 0), 0)
                      .toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={12} md={4}>
              <Button
                onClick={() => navigateToPrint(`${selectedType} Report`)}
                variant="contained"
                color="primary"
                fullWidth
              >
                Print
              </Button>
            </Grid>
          </Grid>
        </DashboardCard>
      </PageContainer>
    </>
  );
}

export default SalesReport;
